import { RollupConfig } from '@/types/rollup'

export const ALL_COMPANIES_LABEL = 'All Companies'

export type RollupConfigEntry = {
  config: RollupConfig
  isDraft: boolean
  /** Whether this config's row on the server (before any staged vendor
   * edit is substituted into `config`) has zero vendors. Always false for a
   * draft, which has no server row yet. Slot ownership for the
   * vendor-required check below must be decided from this, not from
   * `config.vendors` — otherwise an unsaved edit on one card can steal the
   * "All Vendors" slot from a genuinely-saved vendor-less sibling. */
  isVendorlessOnServer: boolean
  /** vendor_nums on this config's server row (before any staged edit).
   * Empty for a draft. Used to keep a vendor claimed for its owning config
   * until a staged removal is actually saved — see
   * `computeClaimedVendorNumsByConfigId`. */
  serverVendorNums: Set<string>
}

export type RollupConfigGroup = {
  dpOrgId: string | null
  companyName: string
  entries: RollupConfigEntry[]
}

/**
 * In-group sort key. Read from the *server* row via `isVendorlessOnServer`,
 * never from the effective (possibly edited) vendor list: staging a vendor
 * edit must not move the card the user is editing. A draft is never
 * vendor-less on the server, so it sorts as vendor-specific from the moment it
 * appears and holds that slot once its first vendor is picked.
 */
function hasVendorsOnServer(entry: RollupConfigEntry): number {
  return entry.isVendorlessOnServer ? 0 : 1
}

/**
 * Groups entries by company and orders both the groups and the rows within
 * each group. Callers must pass `entries` pre-ordered oldest-first — saved
 * configs in API (creation) order, then drafts appended in creation order —
 * since that relative order is preserved by the in-group sort below and is
 * also what `computeVendorRequiredConfigIds` relies on to find the "older"
 * vendor-less config in a company.
 */
export function groupRollupConfigEntries(
  entries: RollupConfigEntry[],
  companyNameById: Map<string, string>,
): RollupConfigGroup[] {
  const groupsByKey = new Map<string, RollupConfigGroup>()

  entries.forEach((entry) => {
    const dpOrgId = entry.config.dp_org_id
    const key = dpOrgId ?? ''
    let group = groupsByKey.get(key)
    if (!group) {
      group = {
        dpOrgId,
        companyName:
          dpOrgId === null
            ? ALL_COMPANIES_LABEL
            : companyNameById.get(dpOrgId) || dpOrgId,
        entries: [],
      }
      groupsByKey.set(key, group)
    }
    group.entries.push(entry)
  })

  // Vendor-less (All Vendors) config first within a group; Array.sort is
  // stable, so this preserves the oldest-first order passed in otherwise.
  groupsByKey.forEach((group) => {
    group.entries = [...group.entries].sort(
      (a, b) => hasVendorsOnServer(a) - hasVendorsOnServer(b),
    )
  })

  return Array.from(groupsByKey.values()).sort((a, b) => {
    if (a.dpOrgId === null) return -1
    if (b.dpOrgId === null) return 1
    return a.companyName.localeCompare(b.companyName)
  })
}

/**
 * The server allows one vendor-less config per (project, company), so a
 * company config with no effective vendors can't be saved unless it owns
 * that "All Vendors" slot. Returns the ids that don't.
 *
 * Ownership follows *server* state, never a staged edit: an entry that is
 * vendor-less on the server holds the slot until its edit is actually saved,
 * in both directions. Only when no entry is vendor-less on the server does
 * the oldest effectively-vendor-less one win (`entries` must be oldest-first
 * — see `groupRollupConfigEntries`), which settles two drafts racing.
 */
export function computeVendorRequiredConfigIds(
  entries: RollupConfigEntry[],
): Set<string> {
  const slotCandidatesByDpOrgId = new Map<string, RollupConfigEntry[]>()

  entries.forEach((entry) => {
    const { config, isVendorlessOnServer } = entry
    // All Companies configs don't target vendors, so the vendor-required
    // state doesn't apply to them.
    if (config.dp_org_id === null) return
    // Contenders for the company's "All Vendors" slot: everything with no
    // *effective* vendors, plus anything still vendor-less *on the server*
    // (i.e. an owner holding the slot behind an unsaved addition).
    if (config.vendors.length > 0 && !isVendorlessOnServer) return

    const list = slotCandidatesByDpOrgId.get(config.dp_org_id) ?? []
    list.push(entry)
    slotCandidatesByDpOrgId.set(config.dp_org_id, list)
  })

  const vendorRequiredIds = new Set<string>()

  slotCandidatesByDpOrgId.forEach((list) => {
    const serverOwner = list.find((entry) => entry.isVendorlessOnServer)
    const ownerId = (serverOwner ?? list[0]).config.id
    list.forEach((entry) => {
      // A candidate that only holds the slot via its server row isn't itself
      // in the vendor-required state — it has staged vendors to save.
      if (entry.config.vendors.length > 0) return
      if (entry.config.id !== ownerId) vendorRequiredIds.add(entry.config.id)
    })
  })

  return vendorRequiredIds
}

export function rollupConfigTitle(
  config: RollupConfig,
  isVendorRequired: boolean,
): string {
  if (config.dp_org_id === null) return ALL_COMPANIES_LABEL

  if (config.vendors.length === 0) {
    return isVendorRequired ? 'New config' : 'All Vendors'
  }

  // Migrated vendors can legitimately have a blank display_name pending a
  // lazy backfill — fall back to vendor_num, same as RollupConfigVendors.
  const names = config.vendors.map(
    (vendor) => vendor.display_name || vendor.vendor_num,
  )
  if (names.length <= 3) return names.join(', ')
  return `${names.slice(0, 3).join(', ')} +${names.length - 3} more`
}

export function rollupConfigSubtitle(
  config: RollupConfig,
  isVendorRequired: boolean,
): string {
  if (config.dp_org_id === null) {
    return 'Default config · applies to all vendors'
  }

  if (config.vendors.length === 0) {
    return isVendorRequired
      ? 'No vendors selected yet'
      : 'Default config · applies to all vendors for this company'
  }

  return 'Vendor-specific config'
}

/**
 * For every config in a single company group, the vendor_nums targeted by
 * some *other* config in that same group — a vendor_num may only appear in
 * one config per (project, company), so these are the ones to disable in the
 * Add Vendor dialog with an "In another config" hint.
 *
 * Each config's footprint is the *union* of its server vendor_nums and its
 * effective (possibly staged) vendor_nums, not just the effective set — a
 * staged removal must not immediately free up a vendor_num the server still
 * has assigned (the sibling's Save would then be rejected for a duplicate),
 * and conversely a staged addition claims a vendor_num for its new config
 * right away so a sibling can't also stage-add it.
 */
export function computeClaimedVendorNumsByConfigId(
  groupEntries: RollupConfigEntry[],
): Map<string, Set<string>> {
  const footprintByConfigId = new Map<string, Set<string>>()
  groupEntries.forEach(({ config, serverVendorNums }) => {
    const footprint = new Set(serverVendorNums)
    config.vendors.forEach((vendor) => footprint.add(vendor.vendor_num))
    footprintByConfigId.set(config.id, footprint)
  })

  const claimedByConfigId = new Map<string, Set<string>>()
  groupEntries.forEach(({ config }) => {
    const claimed = new Set<string>()
    footprintByConfigId.forEach((footprint, otherConfigId) => {
      if (otherConfigId === config.id) return
      footprint.forEach((vendorNum) => claimed.add(vendorNum))
    })
    claimedByConfigId.set(config.id, claimed)
  })

  return claimedByConfigId
}

/**
 * The card's unsaved-work label, or null when there's nothing unsaved. A draft
 * has never been persisted at all, so it outranks a merely dirty card.
 *
 * Being disabled is tracked separately and shown alongside this rather than
 * instead of it — the two are independent, and a disabled config with unsaved
 * edits needs to say both.
 */
export function rollupConfigDraftLabel({
  isDraft,
  isDirty,
}: {
  isDraft: boolean
  isDirty: boolean
}): string | null {
  if (isDraft) return 'Draft — never saved'
  if (isDirty) return 'Unsaved changes'
  return null
}
